import {LeftOutlined, RightOutlined} from '@ant-design/icons'
import {Button} from 'antd'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {PlantCard, PlantCardProps} from '../plantsGallery/PlantCard'

export type CardSliderProps = {
  plants: PlantCardProps[]
  slidesPerView: number
}

function getByIndexWithCyclicWrap<T>(array: T[], index: number) {
  const {length} = array
  while (index < 0) {
    index += length
  }

  while (index >= length) {
    index -= length
  }

  return array[index]
}

export function CardSlider({plants, slidesPerView}: CardSliderProps) {
  const [firstCardIndex, setFirstCardIndex] = useState(0)
  const [animationOffset, setAnimationOffset] = useState<number>(0)
  const [animationEnabled, setAnimationEnabled] = useState(false)
  const sliderContainerRef = useRef<HTMLDivElement>()

  const plantCopyBufferLength = 2

  const plantsToDisplay: PlantCardProps[] = []

  const rerenderCards = useCallback(
    (offset: number) => {
      setFirstCardIndex((current) => {
        let newValue = current + offset
        while (newValue < 0) {
          newValue += plants.length
        }

        while (newValue >= length) {
          newValue -= plants.length
        }

        return newValue
      })
    },
    [setFirstCardIndex, plants.length]
  )

  const slideCards = useCallback(
    (direction: 'left' | 'right') => {
      setAnimationEnabled(true)
      requestAnimationFrame(() => {
        if (!sliderContainerRef.current) {
          return
        }

        const cardWidth =
          sliderContainerRef.current.clientWidth /
          (slidesPerView + plantCopyBufferLength + plantCopyBufferLength)

        setAnimationOffset(direction === 'left' ? cardWidth : -cardWidth)
      })
    },
    [setAnimationOffset]
  )

  for (
    let i = firstCardIndex - plantCopyBufferLength;
    i < firstCardIndex + slidesPerView + plantCopyBufferLength;
    i++
  ) {
    const plant = getByIndexWithCyclicWrap(plants, i)
    plantsToDisplay.push(plant)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'hidden',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          transition: animationEnabled ? 'transform .5s' : undefined,
          transform: `translateX(${animationOffset}px)`,
        }}
        ref={sliderContainerRef}
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget) {
            rerenderCards(animationOffset < 0 ? 1 : -1)
            setAnimationEnabled(false)
            setAnimationOffset(0)
          }
        }}
      >
        {plantsToDisplay.map((plant, index) => (
          <div style={{display: 'flex', flexDirection: 'row'}} key={`${plant.name}-${index}`}>
            <PlantCard
              photo={plant.photo}
              sun={plant.sun}
              watering={plant.watering}
              name={plant.name}
              description={plant.description}
            ></PlantCard>
          </div>
        ))}
      </div>

      <div
        style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 0'}}
      >
        <Button
          type="text"
          style={{color: '#2F5D55'}}
          onClick={() => {
            if (animationEnabled) {
              return
            }
            slideCards('left')
          }}
        >
          <LeftOutlined />
        </Button>
        <Button
          type="text"
          style={{color: '#2F5D55'}}
          onClick={() => {
            if (animationEnabled) {
              return
            }
            slideCards('right')
          }}
        >
          <RightOutlined />
        </Button>
      </div>
    </div>
  )
}
